import React, { Component } from "react";
import axios from "axios";
import Loader from "react-loader-spinner";
import ConfirmationDialog from "./ConfirmationDialog";
import TicketModal from "../BoseComplaintComponent/TicketModal";
import { newBackendBaseUrl } from "../../config";
import "./RaiseComplaintForm.css";

/**
 * Generic "Raise Complaint" form used by the Complaints (MAC/TWS) and Noise
 * Junior sections (NAS-5998). Unlike the Master Buds form
 * (BoseComplaintComponent/RaiseComplaint.js) this version:
 *   - exposes Product Group as a live dropdown (data-driven from the NC catalog)
 *     instead of hardcoding group 21, and
 *   - has NO serial-number field/validation (serial number stays exclusive to
 *     Master Buds).
 * The selected product_group_id is submitted to /complaint/add/bose; the backend
 * routes/tags the ticket by group.
 *
 * Props:
 *   setTeamName(value)      - resets the parent page's active tab after submit.
 *   redirectPath            - route the success modal returns to (e.g. "/complaints-data").
 *   defaultProductGroupId   - optional: pre-select a Product Group on mount (AC-9).
 *   lockProductGroup        - optional: disable the Product Group dropdown (used by the
 *                             Noise Junior tab, which is locked to its group).
 *   excludeGroupIds         - optional: array of product_group_ids to hide from the
 *                             dropdown (e.g. Master Buds/Junior on the Complaints tab).
 */
class RaiseComplaintForm extends Component {
  state = {
    // Cascading catalog data
    groupOptions: [],
    dropdownOptions: [], // channels
    productOptions: [],
    colorOptions: [],
    problemOptions: [],
    subProblemOptions: [],
    stateOptions: [],
    cityOptions: [],

    // Selections
    selectedGroup: "",
    selectedGroupName: "",
    selectedChannel: "",
    selectedChannelName: "",
    selectedProduct: "",
    selectedProductName: "",
    selectedColor: "",
    selectedColorName: "",
    selectedProblem: "",
    selectedProblemName: "",
    selectedSubProblem: "",
    selectedSubProblemName: "",

    complaintDescription: "",
    purchaseDate: "",

    // User details
    name: "",
    phoneNumber: "",
    email: "",
    addressLine1: "",
    addressLine2: "",
    pincode: "",
    state: "",
    selectedStateName: "",
    stateId: null,
    city: "",
    cityId: null,
    selectedCityName: "",

    isConfirmationOpen: false,
    isTicketModalOpen: false,
    ticketId: null,
    isLoading: false,
  };

  componentDidMount() {
    this.fetchGroups();
    this.fetchDropdownData();
    this.fetchStates();
  }

  // Posts a query to the connect-product GraphQL endpoint and returns the `data`
  // object. Throws on a GraphQL-level error so callers can handle it like a
  // failed axios request. (Same contract as the Master Buds form.)
  gqlQuery = async (query, variables = {}) => {
    const token = localStorage.getItem("token");
    const response = await axios.post(
      newBackendBaseUrl + "/connect-product/v1/graphql",
      { query, variables },
      { headers: { "x-access-token": token } }
    );
    if (response.data.errors && response.data.errors.length) {
      throw new Error(response.data.errors[0].message);
    }
    return response.data.data;
  };

  fetchGroups = async () => {
    try {
      const data = await this.gqlQuery(
        `query {
          groups {
            id
            group_name
          }
        }`
      );
      // Hide any groups the host section shouldn't offer (e.g. Master Buds (21)
      // and Noise Junior (22) are excluded from the Complaints tab).
      const exclude = this.props.excludeGroupIds || [];
      const groups = (data.groups || []).filter(
        (g) => !exclude.includes(g.id)
      );
      this.setState({ groupOptions: groups }, () => {
        // Optional sidebar-driven pre-selection (AC-9). Agent can still change it
        // unless lockProductGroup is set.
        const preId = this.props.defaultProductGroupId;
        if (preId) {
          const preGroup = this.state.groupOptions.find(
            (g) => g.id === parseInt(preId, 10)
          );
          if (preGroup) {
            this.setState(
              {
                selectedGroup: String(preGroup.id),
                selectedGroupName: preGroup.group_name,
              },
                () => {
                this.fetchProductData();
                this.fetchProblemData();
              }
            );
          }
        }
      });
    } catch (error) {
      console.error("Error fetching product groups:", error);
    }
  };

  fetchDropdownData = async () => {
    try {
      const response = await axios.post(
        newBackendBaseUrl + "/connect-complaint/v1/registration/channel/list"
      );
      this.setState({ dropdownOptions: response.data.data });
    } catch (error) {
      console.error("Error fetching channels:", error);
    }
  };

  fetchProductData = async () => {
    if (!this.state.selectedGroup) return;
    const groupId = parseInt(this.state.selectedGroup, 10);
    const data = await this.gqlQuery(
      `query ($productGroupIds: [Int!]) {
        products(filter: { productGroupIds: $productGroupIds }) {
          id
          product_name
          product_group_id
        }
      }`,
      { productGroupIds: [groupId] }
    );
    this.setState({ productOptions: data.products });
  };

  fetchColorData = async () => {
    if (!this.state.selectedProduct) return;
    const productId = parseInt(this.state.selectedProduct, 10);
    const data = await this.gqlQuery(
      `query ($productIds: [Int!]) {
        productColors(filter: { productIds: $productIds }) {
          id
          color_name
        }
      }`,
      { productIds: [productId] }
    );
    this.setState({ colorOptions: data.productColors });
  };

  fetchProblemData = async () => {
    if (!this.state.selectedGroup) return;
    const groupId = parseInt(this.state.selectedGroup, 10);
    const data = await this.gqlQuery(
      `query ($productGroupIds: [Int!]) {
        problems(filter: { productGroupIds: $productGroupIds }) {
          id
          problem
        }
      }`,
      { productGroupIds: [groupId] }
    );
    this.setState({ problemOptions: data.problems });
  };

  fetchSubProblems = async (problemId) => {
    if (!problemId) return;
    try {
      const data = await this.gqlQuery(
        `query ($problemIds: [Int!]) {
          subProblems(filter: { problemIds: $problemIds }) {
            id
            sub_problem
          }
        }`,
        { problemIds: [problemId] }
      );
      this.setState({ subProblemOptions: data.subProblems });
    } catch (error) {
      console.error("Error fetching sub-problems:", error);
    }
  };

  fetchStates = async () => {
    try {
      const response = await axios.post(
        newBackendBaseUrl + "/connect-master/v1/state/list",
        { country_id: 1 }
      );
      this.setState({ stateOptions: response.data.data });
    } catch (error) {
      console.error("Error fetching states:", error);
    }
  };

  fetchCities = async (stateId) => {
    if (!stateId) return;
    try {
      const response = await axios.post(
        newBackendBaseUrl + "/connect-master/v1/city/list",
        { state_id: stateId }
      );
      this.setState({ cityOptions: response.data.data });
    } catch (error) {
      console.error("Error fetching cities:", error);
    }
  };

  // Changing Product Group resets Product, Color and Problem (AC-7). Customer /
  // address / description fields are intentionally NOT reset.
  handleGroupChange = (event) => {
    const selectedId = event.target.value;
    const selectedItem = this.state.groupOptions.find(
      (item) => item.id == selectedId
    );
    const selectedName = selectedItem ? selectedItem.group_name : "";
    this.setState(
      {
        selectedGroup: selectedId,
        selectedGroupName: selectedName,
        selectedProduct: "",
        selectedProductName: "",
        selectedColor: "",
        selectedColorName: "",
        selectedProblem: "",
        selectedProblemName: "",
        selectedSubProblem: "",
        selectedSubProblemName: "",
        productOptions: [],
        colorOptions: [],
        problemOptions: [],
        subProblemOptions: [],
      },
      () => {
        this.fetchProductData();
        this.fetchProblemData();
      }
    );
  };

  handleChannelChange = (event) => {
    const selectedId = event.target.value;
    const selectedItem = this.state.dropdownOptions.find(
      (item) => item.id == selectedId
    );
    const selectedName = selectedItem ? selectedItem.name : "";
    this.setState({
      selectedChannel: selectedId,
      selectedChannelName: selectedName,
    });
  };

  // Changing Product resets Color only (AC-7).
  handleProductChange = (event) => {
    const selectedId = event.target.value;
    const selectedItem = this.state.productOptions.find(
      (item) => item.id == selectedId
    );
    const selectedName = selectedItem ? selectedItem.product_name : "";
    this.setState(
      {
        selectedProduct: selectedId,
        selectedColor: "",
        selectedColorName: "",
        colorOptions: [],
        selectedProductName: selectedName,
      },
      this.fetchColorData
    );
  };

  handleColorChange = (event) => {
    const selectedId = event.target.value;
    const selectedItem = this.state.colorOptions.find(
      (item) => item.id == selectedId
    );
    const selectedName = selectedItem ? selectedItem.color_name : "";
    this.setState({
      selectedColor: selectedId,
      selectedColorName: selectedName,
    });
  };

  handleProblemChange = (event) => {
    const selectedId = event.target.value;
    const selectedItem = this.state.problemOptions.find(
      (item) => item.id == parseInt(selectedId, 10)
    );
    const selectedName = selectedItem ? selectedItem.problem : "";
    this.setState(
      {
        selectedProblem: selectedItem ? selectedItem.id : "",
        subProblemOptions: [],
        selectedSubProblem: "",
        selectedSubProblemName: "",
        selectedProblemName: selectedName,
      },
      () => selectedItem && this.fetchSubProblems(selectedItem.id)
    );
  };

  handleSubProblemChange = (event) => {
    const selectedId = event.target.value;
    const selectedItem = this.state.subProblemOptions.find(
      (item) => item.id == selectedId
    );
    const selectedName = selectedItem ? selectedItem.sub_problem : "";
    this.setState({
      selectedSubProblem: selectedId,
      selectedSubProblemName: selectedName,
    });
  };

  handleDateChange = (event) => {
    this.setState({ purchaseDate: event.target.value });
  };

  handleStateChange = (event) => {
    const selectedState = this.state.stateOptions.find(
      (item) => item.state_name === event.target.value
    );
    const selectedName = event.target.value;
    if (selectedState) {
      this.setState(
        {
          state: selectedState.state_name,
          stateId: selectedState.id,
          city: "",
          cityId: null,
          cityOptions: [],
          selectedStateName: selectedName,
        },
        () => this.fetchCities(selectedState.id)
      );
    }
  };

  handleCityChange = (event) => {
    const selectedCity = this.state.cityOptions.find(
      (item) => item.city === event.target.value
    );
    const selectedName = event.target.value;
    if (selectedCity) {
      this.setState({
        city: selectedCity.city,
        cityId: selectedCity.id,
        selectedCityName: selectedName,
      });
    }
  };

  handlePhoneNumberChange = (e) => {
    const input = e.target.value;
    if (/^\d{0,10}$/.test(input)) {
      this.setState({ phoneNumber: input });
    }
  };

  handleEmailChange = (e) => {
    this.setState({ email: e.target.value.trim() });
  };

  handleSubmit = (e) => {
    e.preventDefault();
    // No serial-number validation for these product groups — open the review
    // dialog directly.
    this.setState({ isConfirmationOpen: true });
  };

  handleConfirmSubmit = async () => {
    this.setState({ isLoading: true });
    const formData = new FormData();
    formData.append("name", this.state.name);
    formData.append("email", this.state.email);
    formData.append("mobile", this.state.phoneNumber);
    formData.append("product_problem_id", this.state.selectedProblem);
    formData.append("problem_description", this.state.complaintDescription);
    formData.append("sub_product_problem_id", this.state.selectedSubProblem || "");
    formData.append("product_id", this.state.selectedProduct);
    formData.append("product_color_id", this.state.selectedColor);
    formData.append("purchase_date", this.state.purchaseDate);
    formData.append("channel_id", this.state.selectedChannel);
    formData.append("image", null);
    formData.append("invoice", null);
    formData.append("address", this.state.addressLine1);
    formData.append("address2", this.state.addressLine2);
    formData.append("pincode", this.state.pincode);
    formData.append("state_id", this.state.stateId);
    formData.append("city_id", this.state.cityId);
    formData.append("product_group_id", this.state.selectedGroup);
    // Serial number is not collected for these groups; send empty for parity
    // with the backend's expected field set.
    formData.append("serial_number", "");
    formData.append("raisedByAgent", true);

    // Send the operator's token so the backend can attribute the agent-raised
    // note ("Complaint raised by agent - <name>") on the new complaint.
    const config = {
      headers: {
        "Content-Type": "multipart/form-data",
        "x-access-token": localStorage.getItem("token"),
      },
    };

    try {
      const response = await axios.post(
        newBackendBaseUrl + "/connect-complaint/v1/complaint/add/bose",
        formData,
        config
      );
      this.setState({
        isConfirmationOpen: false,
        isTicketModalOpen: true,
        ticketId: response.data.data.ticket_id,
        isLoading: false,
      });
    } catch (error) {
      console.error("Error submitting complaint:", error);
      this.setState({
        isConfirmationOpen: false,
        isLoading: false,
      });
      alert("Failed to submit complaint. Please try again.");
    }
  };

  render() {
    const {
      groupOptions,
      dropdownOptions,
      productOptions,
      colorOptions,
      problemOptions,
      selectedGroup,
      selectedChannel,
      selectedProduct,
      selectedColor,
      selectedProblem,
      complaintDescription,
      purchaseDate,
    } = this.state;

    return (
      <div className="raise-complaint-form">
        {this.state.isLoading && (
          <div className="loader-overlay">
            <Loader type="Oval" color="#00BFFF" height={80} width={80} />
          </div>
        )}

        <TicketModal
          isOpen={this.state.isTicketModalOpen}
          onClose={() => this.setState({ isTicketModalOpen: false })}
          ticketId={this.state.ticketId}
          onSetTeamName={this.props.setTeamName}
          redirectPath={this.props.redirectPath}
        />

        <ConfirmationDialog
          isOpen={this.state.isConfirmationOpen}
          onClose={() => this.setState({ isConfirmationOpen: false })}
          onConfirm={this.handleConfirmSubmit}
          formData={{
            name: this.state.name,
            email: this.state.email,
            mobile: this.state.phoneNumber,
            productGroup: this.state.selectedGroupName,
            channelName: this.state.selectedChannelName,
            productName: this.state.selectedProductName,
            colorName: this.state.selectedColorName,
            problemName: this.state.selectedProblemName,
            subProblemName: this.state.selectedSubProblemName,
            description: this.state.complaintDescription,
            purchaseDate: this.state.purchaseDate,
            address: this.state.addressLine1,
            address2: this.state.addressLine2,
            pincode: this.state.pincode,
            cityName: this.state.selectedCityName,
            stateName: this.state.selectedStateName,
          }}
        />

        <form onSubmit={this.handleSubmit}>
          <div className="form-container">
            {/* Left Column - Product Details */}
            <div className="form-column">
              <div className="form-group">
                <label>Product Group*</label>
                <select
                  value={selectedGroup}
                  onChange={this.handleGroupChange}
                  disabled={this.props.lockProductGroup}
                  required
                >
                  <option value="">-- Select Product Group --</option>
                  {groupOptions.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.group_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Select Channel of Purchase*</label>
                <select
                  value={selectedChannel}
                  onChange={this.handleChannelChange}
                  required
                >
                  <option value="">-- Select --</option>
                  {dropdownOptions.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Select Product*</label>
                <select
                  value={selectedProduct}
                  onChange={this.handleProductChange}
                  disabled={!selectedGroup}
                  required
                >
                  <option value="">-- Select Product --</option>
                  {productOptions.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.product_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Select Product Color*</label>
                <select
                  value={selectedColor}
                  onChange={this.handleColorChange}
                  disabled={!selectedProduct}
                  required
                >
                  <option value="">-- Select Color --</option>
                  {colorOptions.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.color_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Select Problem*</label>
                <select
                  value={selectedProblem}
                  onChange={this.handleProblemChange}
                  disabled={!selectedGroup}
                  required
                >
                  <option value="">-- Select Problem --</option>
                  {problemOptions.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.problem}
                    </option>
                  ))}
                </select>
              </div>

              {this.state.subProblemOptions.length > 0 && (
                <div className="form-group">
                  <label>Select Sub-Problem*</label>
                  <select
                    value={this.state.selectedSubProblem}
                    onChange={this.handleSubProblemChange}
                    required
                  >
                    <option value="">-- Select Sub-Problem --</option>
                    {this.state.subProblemOptions.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.sub_problem}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>Complaint Description (Max 250 characters)*</label>
                <textarea
                  value={complaintDescription}
                  onChange={(e) => {
                    const value = e.target.value.replace(/^\s+/, "");
                    if (value.length <= 250) {
                      this.setState({ complaintDescription: value });
                    }
                  }}
                  placeholder="Describe the issue..."
                  maxLength="250"
                  rows="3"
                  required
                />
                <small className="char-counter">
                  {250 - complaintDescription.length} characters remaining
                </small>
              </div>

              <div className="form-group">
                <label>Date of Purchase*</label>
                <input
                  type="date"
                  value={purchaseDate}
                  onChange={this.handleDateChange}
                  max={new Date().toISOString().split("T")[0]}
                  required
                />
              </div>
            </div>

            {/* Right Column - User Details */}
            <div className="form-column">
              <div className="form-group">
                <label>Customer Name*</label>
                <input
                  type="text"
                  value={this.state.name}
                  onChange={(e) =>
                    this.setState({ name: e.target.value.trimStart() })
                  }
                  onBlur={(e) => this.setState({ name: e.target.value.trim() })}
                  placeholder="Enter customer name"
                  required
                />
              </div>

              <div className="form-group">
                <label>Phone Number*</label>
                <input
                  type="tel"
                  value={this.state.phoneNumber}
                  onChange={this.handlePhoneNumberChange}
                  placeholder="Enter customer phone number"
                  required
                />
              </div>

              <div className="form-group">
                <label>Email*</label>
                <input
                  type="email"
                  value={this.state.email}
                  onChange={this.handleEmailChange}
                  placeholder="Enter customer email"
                  required
                />
              </div>

              <div className="form-group">
                <label>Address Line 1*</label>
                <input
                  type="text"
                  value={this.state.addressLine1}
                  onChange={(e) =>
                    this.setState({
                      addressLine1: e.target.value.replace(/^\s+/, ""),
                    })
                  }
                  placeholder="Enter address line 1"
                  required
                />
              </div>

              <div className="form-group">
                <label>Address Line 2</label>
                <input
                  type="text"
                  value={this.state.addressLine2}
                  onChange={(e) =>
                    this.setState({
                      addressLine2: e.target.value.replace(/^\s+/, ""),
                    })
                  }
                  placeholder="Enter address line 2"
                />
              </div>

              <div className="form-group">
                <label>Pincode*</label>
                <input
                  type="text"
                  value={this.state.pincode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    if (value.length <= 6) {
                      this.setState({ pincode: value });
                    }
                  }}
                  onBlur={(e) => {
                    if (e.target.value.length !== 6) {
                      alert("Pincode must be exactly 6 digits.");
                      this.setState({ pincode: "" });
                    }
                  }}
                  placeholder="Enter pincode"
                  required
                />
              </div>

              <div className="form-group">
                <label>State*</label>
                <select
                  value={this.state.state}
                  onChange={this.handleStateChange}
                  required
                >
                  <option value="">-- Select State --</option>
                  {this.state.stateOptions?.map((item) => (
                    <option key={item.id} value={item.state_name}>
                      {item.state_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>City*</label>
                <select
                  value={this.state.city}
                  onChange={this.handleCityChange}
                  required
                  disabled={!this.state.state}
                >
                  <option value="">-- Select City --</option>
                  {this.state.cityOptions?.map((item) => (
                    <option key={item.id} value={item.city_name}>
                      {item.city}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <button type="submit">Submit</button>
        </form>
      </div>
    );
  }
}

export default RaiseComplaintForm;
